import { Avatar } from 'module/ui/components/atoms/avatar';
import { HumanisedDate } from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { Switch } from 'module/ui/components/atoms/switch/switch';
import { DraggableTable } from 'module/ui/components/molecules/draggable-table';
import { TableBody } from 'module/ui/components/molecules/draggable-table/styles';
import { ColumnType } from 'module/ui/components/molecules/draggable-table/types';
import {
  faTrash,
  faEdit,
  faGripDotsVertical,
} from '@fortawesome/pro-regular-svg-icons';
import { useNavigate } from '@rocketlane/rocket-router';
import { PageTemplateLite } from 'api/page-template/types';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FlexWrapperWithShortGap } from 'components/common/styles';

const StyledDraggableTable = styled.div`
  ${TableBody} {
    max-height: calc(85vh - 100px);
    overflow-y: auto;
  }
`;

export const PeoplePageTemplateTable: React.FC<{
  templates: PageTemplateLite[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onReorder?: (
    templates: PageTemplateLite[],
    movedTemplateId: number,
    newIndex: number,
  ) => void;
  onStatusChange: (id: string) => void;
  loading?: boolean;
}> = ({ templates, onEdit, onDelete, onReorder, onStatusChange, loading }) => {
  const navigate = useNavigate();

  const columns: ColumnType<PageTemplateLite>[] = [
    {
      key: 'drag',
      title: '',
      width: '24px',
      render: () => (
        <div className="drag-handle">
          <FontAwesomeIcon
            icon={faGripDotsVertical}
            style={{ color: 'var(--gray-50)', cursor: 'grab' }}
          />
        </div>
      ),
    },
    {
      key: 'templateName',
      title: <FormattedMessage id="template_name" />,
      render: (value, _record, _index, _data) => {
        return (
          <FlexWrapperWithShortGap>
            <div>{value}</div>
          </FlexWrapperWithShortGap>
        );
      },
    },
    {
      key: 'lastUpdatedBy',
      title: <FormattedMessage id="last_updated_by" />,
      render: (user) =>
        user ? (
          <>
            <Avatar
              name={`${user.firstName ?? ''} ${user.lastName ?? ''}`}
              size={20}
              src={user.company?.companyLogoUrl}
            />
            {user.firstName} {user.lastName}
          </>
        ) : null,
    },
    {
      key: 'updatedAt',
      title: <FormattedMessage id="last_updated_on" />,
      render: (date) => <HumanisedDate timestamp={moment(date)} />,
    },
    {
      key: 'createdBy',
      title: <FormattedMessage id="created_by" />,
      render: (user) =>
        user ? (
          <>
            <Avatar
              name={`${user.firstName ?? ''} ${user.lastName ?? ''}`}
              size={20}
              src={user.company?.companyLogoUrl}
            />
            {user.firstName} {user.lastName}
          </>
        ) : null,
    },
    {
      key: 'createdAt',
      title: <FormattedMessage id="created_on" />,
      render: (date) => <HumanisedDate timestamp={moment(date)} />,
    },
    {
      key: 'enabled',
      title: <FormattedMessage id="status" />,
      render: (value, record) => (
        <Switch
          checked={value}
          onChange={() => onStatusChange(String(record.templateId))}
          size="small"
          disabled={record.isDefault ?? false}
        />
      ),
    },
    {
      key: 'actions',
      width: '56px',
      title: '',
      render: (_, record) => (
        <MoreActionsDropdown
          menu={{
            onClick: ({ key }) => {
              if (key === 'edit') {
                onEdit(String(record.templateId));
              } else if (key === 'delete') {
                onDelete(String(record.templateId));
              }
            },
            items: [
              {
                type: 'group',
                label: <FormattedMessage id="more_actions" />,
                children: [
                  {
                    key: 'edit',
                    label: <FormattedMessage id="edit" />,
                    icon: <FontAwesomeIcon icon={faEdit} />,
                  },
                  {
                    key: 'delete',
                    label: <FormattedMessage id="delete" />,
                    icon: <FontAwesomeIcon icon={faTrash} />,
                    danger: true,
                  },
                ],
              },
            ],
          }}
        />
      ),
    },
  ];

  return (
    <StyledDraggableTable>
      <DraggableTable<PageTemplateLite & { id: string }>
        columns={columns}
        data={templates.map((template) => ({
          ...template,
          id: String(template.templateId),
        }))}
        onDragEnd={
          onReorder &&
          ((oldIndex, newIndex) => {
            const newTemplates = [...templates];
            const [movedItem] = newTemplates.splice(oldIndex, 1);

            newTemplates.splice(newIndex, 0, movedItem);
            onReorder(newTemplates, movedItem.templateId, newIndex);
          })
        }
        onRowClick={(record) => {
          navigate(`${record?.templateId}`);
        }}
        disableDrag={(record) => record.isDefault ?? false}
        loading={loading}
      />
    </StyledDraggableTable>
  );
};
